import React, { useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Channel } from '../../types/messages';
import toast from 'react-hot-toast';

interface ChannelSettingsModalProps {
  channel: Channel;
  onClose: () => void;
  onDelete: () => void;
}

export default function ChannelSettingsModal({ channel, onClose, onDelete }: ChannelSettingsModalProps) {
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description || '');
  const [type, setType] = useState<Channel['type']>(channel.type);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter a channel name');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Only allow updates if user is channel creator
      if (channel.createdBy !== user.uid) {
        toast.error('You do not have permission to edit this channel');
        return;
      }

      await updateDoc(doc(db, 'channels', channel.id), {
        name: name.trim(),
        description: description.trim(),
        type,
      });

      toast.success('Channel updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating channel:', error);
      toast.error('Failed to update channel');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Only allow deletion if user is channel creator
      if (channel.createdBy !== user.uid) {
        toast.error('You do not have permission to delete this channel');
        return;
      }

      await deleteDoc(doc(db, 'channels', channel.id));
      toast.success('Channel deleted successfully');
      onDelete();
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast.error('Failed to delete channel');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">Channel Settings</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-secondary mb-1">
              Channel Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-text-secondary mb-1">
              Channel Type
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as Channel['type'])}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            >
              <option value="department">Department</option>
              <option value="committee">Committee</option>
              <option value="grade">Grade Level</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div className="pt-4 space-y-4">
            {showDeleteConfirm ? (
              <div className="bg-error-light/20 p-4 rounded-lg">
                <p className="text-error-dark mb-4">
                  Are you sure you want to delete this channel? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-4 py-2 bg-error-DEFAULT text-white rounded-lg hover:bg-error-dark 
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete Channel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full px-4 py-2 text-error-DEFAULT hover:text-error-dark transition-colors 
                         flex items-center justify-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Delete Channel
              </button>
            )}

            <div className="flex justify-end gap-3 border-t border-border-light pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}